import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig, type AxiosError } from "axios";

// Helper function to get CSRF token from cookie
export const getCsrfToken = (): string | null => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const ensureCsrfToken = async (baseUrl: string): Promise<string | null> => {
  const existing = getCsrfToken();
  if (existing) return existing;

  try {
    await axios.get(`${baseUrl}/user/csrf-token`, { withCredentials: true });
  } catch (error) {
    console.error("Failed to fetch CSRF token", error);
    return null;
  }

  return getCsrfToken();
};

const isMutatingMethod = (method?: string): boolean => {
  return !!method && !["get", "head", "options"].includes(method.toLowerCase());
};

type RequestConfigWithRetry = AxiosRequestConfig & {
  _retry?: boolean;
  _csrfRetry?: boolean;
};

export interface AuthInterceptorOptions {
  /** Base URL of the API (used for /user/csrf-token and /user/refresh-token calls). */
  getBaseUrl: () => string;
  /** Redirect target on hard auth failure. Default: "/login". */
  loginPath?: string;
  /** Paths matching this pattern never trigger a login redirect. Default: main app auth pages. */
  authPagePattern?: RegExp;
  /** localStorage key cleared on auth failure. Default: "user". Pass null to skip. */
  storageKey?: string | null;
  /** Unwrap standardized { success, data } envelopes on successful responses. Default: true. */
  unwrapEnvelope?: boolean;
  /** Extra per-request mutation (e.g. custom headers). */
  onRequest?: (config: InternalAxiosRequestConfig) => void;
}

/**
 * Attaches the shared auth/CSRF interceptor behavior to an axios instance:
 * CSRF token header on mutating requests, single in-flight token refresh with
 * queued retries on 401, one CSRF refetch + retry on 403, and login redirect
 * on hard auth failures.
 */
export function attachAuthInterceptors(instance: AxiosInstance, options: AuthInterceptorOptions): void {
  const {
    getBaseUrl,
    loginPath = "/login",
    authPagePattern = /^(\/login|\/register|\/forgot-password|\/reset-password)\/?$/,
    storageKey = "user",
    unwrapEnvelope = true,
    onRequest,
  } = options;

  // Per-instance refresh state (each attached instance gets its own queue)
  let isRefreshing = false;
  let failedQueue: Array<{
    resolve: () => void;
    reject: (reason: unknown) => void;
  }> = [];

  const processQueue = (error: unknown = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    failedQueue = [];
  };

  const redirectToLogin = () => {
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch {}
    }
    try {
      const currentPath = window.location.pathname;
      if (!authPagePattern.test(currentPath)) {
        window.location.href = loginPath;
      }
    } catch {}
  };

  const refreshToken = async (): Promise<void> => {
    const csrfToken = await ensureCsrfToken(getBaseUrl());
    await axios.post(
      `${getBaseUrl()}/user/refresh-token`,
      {},
      {
        withCredentials: true,
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : undefined,
      }
    );
  };

  // Request interceptor - add CSRF token to mutating calls
  instance.interceptors.request.use((config) => {
    const csrfToken = getCsrfToken();
    if (csrfToken && isMutatingMethod(config.method)) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
    onRequest?.(config);
    return config;
  });

  // Response interceptor with token refresh + CSRF retry support
  instance.interceptors.response.use(
    (response) => {
      // Unwrap standardized API response format: { success: true, data: T }
      if (
        unwrapEnvelope &&
        response.data &&
        typeof response.data === "object" &&
        "success" in response.data &&
        "data" in response.data
      ) {
        response.data = response.data.data;
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as RequestConfigWithRetry | undefined;

      if (!originalRequest) {
        return Promise.reject(error);
      }

      // Log all 401 and 403 errors for debugging
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.error("Auth/CSRF Error:", {
          status: error.response?.status,
          url: originalRequest?.url,
          error: error.response?.data,
          hasAccessToken: typeof document !== "undefined" && !!document.cookie.match(/accessToken=/),
          hasRefreshToken: typeof document !== "undefined" && !!document.cookie.match(/refreshToken=/),
          hasCsrfToken: typeof document !== "undefined" && !!document.cookie.match(/XSRF-TOKEN=/),
        });
      }

      // Handle token expiration
      const errorCode = (error.response?.data as { error?: string } | undefined)?.error;
      const shouldAttemptRefresh =
        error.response?.status === 401 && (errorCode === "Token expired" || errorCode === "Authentication required");

      if (shouldAttemptRefresh) {
        if (originalRequest._retry) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;

          try {
            await refreshToken();
            isRefreshing = false;
            processQueue();
            // Retry the original request
            return instance(originalRequest);
          } catch (refreshError) {
            isRefreshing = false;
            processQueue(refreshError);
            // Refresh failed, redirect to login
            console.error("Token refresh failed:", refreshError);
            redirectToLogin();
            return Promise.reject(refreshError);
          }
        }

        // Queue this request to retry once the in-flight refresh completes.
        return new Promise<void>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => instance(originalRequest));
      }

      // Handle CSRF errors: refetch the token and retry once
      const csrfCode = (error.response?.data as { code?: string } | undefined)?.code;
      if (error.response?.status === 403 && (csrfCode === "CSRF_MISSING" || csrfCode === "CSRF_INVALID")) {
        if (originalRequest._csrfRetry) {
          return Promise.reject(error);
        }

        originalRequest._csrfRetry = true;

        try {
          await axios.get(`${getBaseUrl()}/user/csrf-token`, { withCredentials: true });

          const newCsrfToken = getCsrfToken();
          if (newCsrfToken && isMutatingMethod(originalRequest.method)) {
            originalRequest.headers = originalRequest.headers || {};
            (originalRequest.headers as Record<string, unknown>)["X-CSRF-Token"] = newCsrfToken;
          }

          return instance(originalRequest);
        } catch {
          return Promise.reject(error);
        }
      }

      // Any other 401 is a hard auth failure
      if (error.response?.status === 401) {
        redirectToLogin();
      }

      return Promise.reject(error);
    }
  );
}
